import type { EventBus } from './eventBus';
import type { EntityRegistry } from './entityRegistry';
import type { WeaponSelectionManager } from './weaponSelectionManager';
import type { ChargeStateManager } from './chargeStateManager';
import type { TargetingSystem } from './targetingSystem';
import type { GameState } from '../logic/gameState';
import type { ModeConfig } from './gameModes';
import type { Vector2 } from './constants';
import type { Worm } from '../entities/worm';
import type { Entity } from '../entities/entity';
import {
  getWeaponDelay,
  type WeaponDefinition,
  type GameWorld,
  type WeaponResult,
} from '../weapons/types';
import type { Projectile } from '../entities/projectile';
import type { Mine } from '../weapons/mine';
import type { HomingMissile } from '../weapons/homingMissile';
import type { Sheep } from '../weapons/sheep';
import type { MiniRaon } from '../weapons/raon';
import type { SuperSheep } from '../weapons/superSheep';
import type { Kamikaze } from '../weapons/kamikaze';
import type { NapalmDroplet } from '../entities/napalmDroplet';

export interface WeaponExecutorDependencies {
  eventBus: EventBus;
  entityRegistry: EntityRegistry;
  weaponSelection: WeaponSelectionManager;
  chargeState: ChargeStateManager;
  targetingSystem: TargetingSystem;
  gameState: GameState;
  mode: ModeConfig;
}

export interface WeaponExecutorCallbacks {
  getWorld: () => GameWorld;
  canFireWeapon: () => boolean;
  isWaitingForCrates: () => boolean;
  canSwitchWeapon: (weaponId: string) => boolean;
  addProjectile: (projectile: Projectile) => void;
  addMine: (mine: Mine) => void;
  addHomingMissile: (missile: HomingMissile) => void;
  addSheep: (sheep: Sheep) => void;
  addRaon: (raon: MiniRaon) => void;
  addSuperSheep: (superSheep: SuperSheep) => void;
  addKamikaze: (kamikaze: Kamikaze, worm: Worm) => void;
  addNapalmDroplet: (droplet: NapalmDroplet) => void;
  placeGirder: (x: number, y: number) => void;
  activateTool: (tool: 'drill' | 'blowtorch') => void;
  activateJetpack: (worm: Worm) => void;
  selectWeapon: (weaponId: string) => void;
  setPendingSheepTurnEnd: (value: boolean) => void;
  setThrownWorm: (worm: Worm | null) => void;
  setCameraMode: (mode: string) => void;
  setLastAttackingTeam: (teamId: string | null, weaponName: string) => void;
  updateWeaponPanelAmmo: (team: unknown) => void;
  exitTargetingMode: () => void;
  broadcastWeaponFired: (weaponId: string) => void;
  playWeaponSound: (weaponId: string) => void;
  trackAnalytics: (weaponId: string) => void;
  /** Dual item: schedule the automatic duplicate shot (~0.45s later). */
  scheduleDualShot: (request: DualShotRequest) => void;
  /** Spawn a pre-built entity after a delay (e.g. Aduka's staggered shots). */
  scheduleEntity: (delayMs: number, entity: Entity) => void;
}

export type FireResult = boolean | 'delayed';

export interface LastShotInfo {
  angleDeg: number;
  power: number;
  weaponId: string;
}

/** Duplicate shot due to the Dual item (fired automatically by the host runtime). */
export interface DualShotRequest {
  worm: Worm;
  weapon: WeaponDefinition;
  power: number;
  /** Homing shots: target to re-lock before the duplicate. */
  lockedTarget?: Vector2;
  /** Target-fired weapons: refire at the same target. */
  target?: Vector2;
}

export class WeaponExecutor {
  private deps: WeaponExecutorDependencies;
  private callbacks: WeaponExecutorCallbacks;
  /** Last ballistic shot per worm (angle/power memory for the HUD). */
  private lastShotByWorm = new Map<string, LastShotInfo>();

  constructor(deps: WeaponExecutorDependencies, callbacks: WeaponExecutorCallbacks) {
    this.deps = deps;
    this.callbacks = callbacks;
  }

  getLastShot(wormId?: string): LastShotInfo | undefined {
    return wormId ? this.lastShotByWorm.get(wormId) : undefined;
  }

  fire(worm: Worm): FireResult {
    if (this.tryDetonateSheep()) return true;

    const weapon = this.deps.weaponSelection.getSelectedWeapon();
    if (!weapon || !this.deps.gameState.canFire()) return false;
    if (!this.callbacks.canFireWeapon() || this.callbacks.isWaitingForCrates()) return false;
    if (worm.hasPendingDamage()) return false;

    if (!this.callbacks.canSwitchWeapon(weapon.id)) {
      return false;
    }

    const team = this.deps.gameState.teams.find((t) => t.id === worm.teamId);
    if (team && !team.hasAmmo(weapon.id)) {
      return false;
    }

    if (this.deps.weaponSelection.isWeaponSelected('shotgun')) {
      return 'delayed';
    }

    const power = this.deps.chargeState.releaseCharge();
    return this.executeWeaponFire(worm, weapon, power);
  }

  executeWeaponFire(worm: Worm, weapon: WeaponDefinition, power: number): boolean {
    const team = this.deps.gameState.teams.find((t) => t.id === worm.teamId);
    if (team && !team.hasAmmo(weapon.id)) {
      return false;
    }

    this.callbacks.setLastAttackingTeam(team?.id ?? null, weapon.name);

    const lockedTarget = this.deps.targetingSystem.getLockedTarget();

    if (weapon.category === 'homing' && !lockedTarget) {
      return false;
    }

    if (weapon.id === 'girder' && !lockedTarget) {
      return false;
    }

    const result = weapon.fire({
      worm,
      world: this.callbacks.getWorld(),
      // Effective angle = relative aim + ground tilt (slope-relative aim);
      // equals aimAngle on flat ground / classic mode.
      angle: worm.getEffectiveAimAngle(),
      power,
      timer: this.deps.chargeState.getFuseTimer(),
      target: lockedTarget ?? undefined,
    });

    if (result.success && (result.entities || result.delayedEntities)) {
      // Every spawned entity for this shot, immediate AND delayed (e.g. Aduka's
      // sequential tracers). Mode tuning below must hit all of them or the
      // delayed shots fly on a different (unscaled) trajectory and scatter.
      const spawned: Array<{ velocity?: { x: number; y: number }; maxDamage?: number }> = [
        ...(result.entities ?? []),
        ...(result.delayedEntities ?? []).map((d) => d.entity),
      ];

      // Mode tuning: scale launch speed (e.g. GunBound full-map shots).
      const speedMult = this.deps.mode.fire.maxPowerMultiplier;
      if (speedMult !== 1) {
        for (const entity of spawned) {
          if (entity.velocity) {
            entity.velocity.x *= speedMult;
            entity.velocity.y *= speedMult;
          }
        }
      }
      // True angle: shots fired outside the full-damage arc are penalized.
      // Power-Up/Blood items boost the next shot's damage.
      const trueAngle = this.deps.mode.aim.trueAngle;
      let mult = this.deps.gameState.consumeShotDamageBoost();
      if (trueAngle && (worm.aimAngle < trueAngle.min || worm.aimAngle > trueAngle.max)) {
        mult *= this.deps.mode.aim.falseAngleDamageMultiplier;
      }
      if (mult !== 1) {
        for (const entity of spawned) {
          if (typeof entity.maxDamage === 'number') {
            entity.maxDamage = Math.round(entity.maxDamage * mult);
          }
        }
      }
      this.handleResultEntities(result, worm);
    }

    if (result.success) {
      this.deps.gameState.addTurnDelay(getWeaponDelay(weapon));
      this.lastShotByWorm.set(worm.id, {
        angleDeg: Math.round(worm.aimAngle),
        power: Math.round(power),
        weaponId: weapon.id,
      });

      if (weapon.id === 'girder' && lockedTarget) {
        this.callbacks.placeGirder(lockedTarget.x, lockedTarget.y);
      }

      this.callbacks.playWeaponSound(weapon.id);
      this.callbacks.broadcastWeaponFired(weapon.id);
      this.deps.targetingSystem.unlockTarget();
      this.callbacks.trackAnalytics(weapon.id);

      if (team) {
        team.useAmmo(weapon.id);
        this.callbacks.updateWeaponPanelAmmo(team);
      }

      this.handleBonusWeapon(result, weapon, worm);
      this.handleHitWorms(result);

      // Dual item: the same shot repeats automatically.
      if (this.deps.gameState.takeDualShotPending()) {
        this.callbacks.scheduleDualShot({
          worm,
          weapon,
          power,
          lockedTarget: lockedTarget ?? undefined,
        });
      }
    }

    return result.success;
  }

  fireWithTarget(worm: Worm, target: Vector2): boolean {
    const weapon = this.deps.weaponSelection.getSelectedWeapon();
    if (!worm || !weapon || !this.deps.gameState.canFire() || this.callbacks.isWaitingForCrates()) {
      return false;
    }
    if (worm.hasPendingDamage()) return false;

    const team = this.deps.gameState.teams.find((t) => t.id === worm.teamId);
    if (team && !team.hasAmmo(weapon.id)) {
      return false;
    }

    const result = weapon.fire({
      worm,
      world: this.callbacks.getWorld(),
      angle: worm.getEffectiveAimAngle(),
      power: this.deps.chargeState.getChargePower(),
      timer: this.deps.chargeState.getFuseTimer(),
      target,
    });

    if (result.success) {
      this.deps.gameState.addTurnDelay(getWeaponDelay(weapon));
      if (weapon.id === 'girder') {
        this.callbacks.placeGirder(target.x, target.y);
      }

      if (result.entities) {
        const mult = this.deps.gameState.consumeShotDamageBoost();
        if (mult !== 1) {
          for (const entity of result.entities as Array<{ maxDamage?: number }>) {
            if (typeof entity.maxDamage === 'number') {
              entity.maxDamage = Math.round(entity.maxDamage * mult);
            }
          }
        }
        this.handleResultEntities(result, worm);
      }

      this.callbacks.playWeaponSound(weapon.id);
      this.callbacks.trackAnalytics(weapon.id);

      this.deps.eventBus.emit('weapon:fired', {
        weaponId: weapon.id,
        wormId: worm.id,
        teamId: worm.teamId,
        angle: worm.aimAngle,
        power: this.deps.chargeState.getChargePower(),
        target,
      });

      if (team) {
        team.useAmmo(weapon.id);
        this.callbacks.updateWeaponPanelAmmo(team);
      }

      if (result.bonusWeapon) {
        this.callbacks.selectWeapon(result.bonusWeapon);
      } else {
        const maxUses = weapon.maxUsesPerTurn;
        const shotsAfterThis = this.deps.gameState.getShotsFired() + 1;
        const shouldEndTurn = maxUses !== -1 && shotsAfterThis >= maxUses;
        this.deps.gameState.onWeaponFired(shouldEndTurn);
      }
      this.callbacks.exitTargetingMode();

      // Dual item: refire at the same target automatically.
      if (this.deps.gameState.takeDualShotPending()) {
        this.callbacks.scheduleDualShot({
          worm,
          weapon,
          power: this.deps.chargeState.getChargePower(),
          target,
        });
      }
    }

    return result.success;
  }

  tryDetonateSheep(): boolean {
    const currentTeam = this.deps.gameState.getCurrentTeam();
    if (!currentTeam) return false;

    const activeSheep = this.deps.entityRegistry.find<Sheep>(
      'sheep',
      (s) => s.active && s.ownerTeamId === currentTeam.id,
    );
    if (activeSheep) {
      activeSheep.detonate();
      return true;
    }

    const activeSuperSheep = this.deps.entityRegistry.find<SuperSheep>(
      'superSheep',
      (s) => s.active && s.ownerTeamId === currentTeam.id,
    );
    if (activeSuperSheep) {
      if (!activeSuperSheep.isFlying) {
        activeSuperSheep.activateFlight();
      } else {
        activeSuperSheep.detonate();
      }
      return true;
    }
    return false;
  }

  tryActivateSuperSheep(): boolean {
    const currentTeam = this.deps.gameState.getCurrentTeam();
    if (!currentTeam) return false;

    const activeSuperSheep = this.deps.entityRegistry.find<SuperSheep>(
      'superSheep',
      (s) => s.active && s.ownerTeamId === currentTeam.id,
    );
    if (activeSuperSheep && !activeSuperSheep.isFlying) {
      activeSuperSheep.activateFlight();
      return true;
    }
    return false;
  }

  playWeaponSound(weaponId: string): void {
    this.callbacks.playWeaponSound(weaponId);
  }

  private handleResultEntities(result: WeaponResult, worm: Worm): void {
    if (result.delayedEntities) {
      for (const { delayMs, entity } of result.delayedEntities) {
        this.callbacks.scheduleEntity(delayMs, entity);
      }
    }

    if (!result.entities) return;

    for (const entity of result.entities) {
      if (this.isProjectile(entity)) {
        this.callbacks.addProjectile(entity as Projectile);
      } else if (this.isMine(entity)) {
        this.callbacks.addMine(entity as Mine);
      } else if (this.isRaon(entity)) {
        this.callbacks.addRaon(entity as MiniRaon);
      } else if (this.isHomingMissile(entity)) {
        this.callbacks.addHomingMissile(entity as HomingMissile);
      } else if (this.isSheep(entity)) {
        this.callbacks.addSheep(entity as Sheep);
      } else if (this.isSuperSheep(entity)) {
        this.callbacks.addSuperSheep(entity as SuperSheep);
      } else if (this.isKamikaze(entity)) {
        this.callbacks.addKamikaze(entity as Kamikaze, worm);
      } else if (this.isNapalmDroplet(entity)) {
        this.callbacks.addNapalmDroplet(entity as NapalmDroplet);
      }
    }
  }

  private handleBonusWeapon(result: WeaponResult, weapon: WeaponDefinition, worm: Worm): void {
    if (result.bonusWeapon === 'drill_active') {
      this.callbacks.activateTool('drill');
    } else if (result.bonusWeapon === 'blowtorch_active') {
      this.callbacks.activateTool('blowtorch');
    } else if (result.bonusWeapon) {
      this.callbacks.selectWeapon(result.bonusWeapon);
    } else if (weapon.id === 'sheep') {
      this.callbacks.setPendingSheepTurnEnd(true);
      this.deps.gameState.onWeaponFired(false);
    } else if (weapon.id === 'supersheep') {
      this.deps.gameState.onWeaponFired(false);
    } else if (weapon.id === 'jetpack') {
      this.callbacks.activateJetpack(worm);
      this.deps.gameState.onWeaponFired(false);
    } else if (weapon.category !== 'utility') {
      const maxUses = weapon.maxUsesPerTurn;
      const shotsAfterThis = this.deps.gameState.getShotsFired() + 1;
      const shouldEndTurn = maxUses !== -1 && shotsAfterThis >= maxUses;
      this.deps.gameState.onWeaponFired(shouldEndTurn);
    }
  }

  private handleHitWorms(result: WeaponResult): void {
    if (result.hitWorms && result.hitWorms.length > 0) {
      this.callbacks.setThrownWorm(result.hitWorms[0]);
      this.callbacks.setCameraMode('thrown');
    }
  }

  private isProjectile(entity: unknown): boolean {
    return (
      entity !== null &&
      typeof entity === 'object' &&
      'type' in entity &&
      'explosionRadius' in entity &&
      !('target' in entity)
    );
  }

  private isMine(entity: unknown): boolean {
    return (
      entity !== null && typeof entity === 'object' && 'armDelay' in entity && 'armed' in entity
    );
  }

  private isHomingMissile(entity: unknown): boolean {
    return (
      entity !== null && typeof entity === 'object' && 'target' in entity && 'homingTimer' in entity
    );
  }

  private isRaon(entity: unknown): boolean {
    return entity !== null && typeof entity === 'object' && 'isRaon' in entity;
  }

  private isSheep(entity: unknown): boolean {
    return (
      entity !== null &&
      typeof entity === 'object' &&
      'walkSpeed' in entity &&
      !('isFlying' in entity) &&
      !('isRaon' in entity)
    );
  }

  private isSuperSheep(entity: unknown): boolean {
    return (
      entity !== null &&
      typeof entity === 'object' &&
      'isFlying' in entity &&
      'flyingAngle' in entity
    );
  }

  private isKamikaze(entity: unknown): boolean {
    return (
      entity !== null &&
      typeof entity === 'object' &&
      'directionX' in entity &&
      'directionY' in entity
    );
  }

  private isNapalmDroplet(entity: unknown): boolean {
    return (
      entity !== null &&
      typeof entity === 'object' &&
      'onLand' in entity &&
      !('target' in entity) &&
      !('explosionRadius' in entity)
    );
  }
}
